"use client";

import { useState, FormEvent, useEffect, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { signIn, confirmNewPassword, requestPasswordReset, confirmPasswordReset, clearAuth } from '@/lib/auth';
import { useAuth } from '@/components/auth-provider';
import { recordLogin } from '@/lib/api';

type View = 'login' | 'reset_request' | 'reset_confirm';

function LoginForm() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requiresNewPassword, setRequiresNewPassword] = useState(false);
  const [view, setView] = useState<View>('login');
  const [resetUsername, setResetUsername] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isLoggedIn, isLoading, checkAuth } = useAuth();

  // 認証済みの場合はリダイレクト
  useEffect(() => {
    if (!isLoading && isLoggedIn) {
      const redirect = searchParams.get('redirect') || '/';
      router.push(redirect);
    }
  }, [isLoggedIn, isLoading, router, searchParams]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      if (requiresNewPassword) {
        await confirmNewPassword(newPassword);
        // confirmNewPassword成功でセッションが確立済みのため再signIn不要
        // リダイレクト前にユーザー種別をチェック（許可されない種別はログインさせない）
        const { allowed } = await recordLogin();
        if (!allowed) {
          await clearAuth();
          setError('このアカウントではログインできません。');
          return;
        }
        await checkAuth();
        const redirect = searchParams.get('redirect') || '/';
        router.push(redirect);
      } else {
        const result = await signIn(username, password);
        if (result.isSignedIn) {
          // リダイレクト前にユーザー種別をチェック（許可されない種別はログインさせない）
          const { allowed } = await recordLogin();
          if (!allowed) {
            await clearAuth();
            setError('このアカウントではログインできません。');
            return;
          }
          await checkAuth();
          const redirect = searchParams.get('redirect') || '/';
          router.push(redirect);
        } else if ('nextStep' in result && result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          setRequiresNewPassword(true);
        } else {
          setError('ログインに失敗しました');
        }
      }
    } catch (err) {
      console.error('ログインエラー:', err);
      setError('ログイン処理中にエラーが発生しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestReset = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await requestPasswordReset(resetUsername);
      setView('reset_confirm');
    } catch (err: any) {
      // セキュリティのため、ユーザーが存在しない場合も次の画面へ進む
      if (err.name === 'UserNotFoundException' || err.name === 'InvalidParameterException') {
        setView('reset_confirm');
      } else if (err.name === 'NotAuthorizedException') {
        setError("初回ログインがまだ完了していません。招待メールに記載の仮パスワードでログインし、パスワードを変更してください。");
      } else {
        setError('エラーが発生しました。しばらく経ってから再度お試しください。');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmReset = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await confirmPasswordReset(resetUsername, resetCode, resetNewPassword);
      setView('login');
      setSuccessMessage('パスワードをリセットしました。新しいパスワードでログインしてください。');
    } catch (err: any) {
      if (err.name === 'CodeMismatchException') {
        setError('確認コードが正しくありません。');
      } else if (err.name === 'ExpiredCodeException') {
        setError('確認コードの有効期限が切れています。再度リセットを行ってください。');
      } else {
        setError('エラーが発生しました。しばらく経ってから再度お試しください。');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-gray-500">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-md">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {view === 'login' ? 'ドライバーアプリ' : 'パスワードリセット'}
          </h2>
          {view === 'login' && (
            <p className="mt-2 text-center text-sm text-gray-600">
              ログインしてください
            </p>
          )}
        </div>

        {view === 'login' && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {successMessage && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                {successMessage}
              </div>
            )}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="username" className="sr-only">
                  ユーザー名
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="ユーザー名"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  パスワード
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className={`appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm ${requiresNewPassword ? '' : 'rounded-b-md'}`}
                  placeholder="パスワード"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              {requiresNewPassword && (
                <div>
                  <label htmlFor="newPassword" className="sr-only">
                    新しいパスワード
                  </label>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    required
                    className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder="新しいパスワード"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
              )}
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'ログイン中...' : requiresNewPassword ? 'パスワードを変更してログイン' : 'ログイン'}
              </button>
            </div>

            {process.env.NEXT_PUBLIC_AUTH_MODE !== 'local' && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setView('reset_request'); setResetUsername(username); setError(''); setSuccessMessage(''); }}
                  className="text-sm text-blue-600 hover:underline"
                >
                  パスワードをお忘れですか？
                </button>
              </div>
            )}
          </form>
        )}

        {view === 'reset_request' && (
          <form className="mt-8 space-y-6" onSubmit={handleRequestReset}>
            <p className="text-sm text-gray-600">
              登録済みのメールアドレスを入力してください。確認コードを送信します。
            </p>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
                {error}
              </div>
            )}
            <div>
              <label htmlFor="resetUsername" className="sr-only">
                メールアドレス
              </label>
              <input
                id="resetUsername"
                type="text"
                required
                className="appearance-none block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="メールアドレス"
                value={resetUsername}
                onChange={(e) => setResetUsername(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? '送信中...' : '確認コードを送信'}
              </button>
            </div>
            <div className="text-center">
              <button
                type="button"
                onClick={() => { setView('login'); setError(''); }}
                className="text-sm text-gray-500 hover:underline"
              >
                ログインに戻る
              </button>
            </div>
          </form>
        )}

        {view === 'reset_confirm' && (
          <form className="mt-8 space-y-6" onSubmit={handleConfirmReset}>
            <p className="text-sm text-gray-600">
              メールに届いた確認コードと新しいパスワードを入力してください。
            </p>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
                {error}
              </div>
            )}
            <div className="space-y-3">
              <input
                id="resetCode"
                type="text"
                required
                className="appearance-none block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="確認コード"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                disabled={isSubmitting}
              />
              <input
                id="resetNewPassword"
                type="password"
                required
                className="appearance-none block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="新しいパスワード"
                value={resetNewPassword}
                onChange={(e) => setResetNewPassword(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'リセット中...' : 'パスワードをリセット'}
              </button>
            </div>
            <div className="text-center">
              <button
                type="button"
                onClick={() => { setView('reset_request'); setError(''); }}
                className="text-sm text-gray-500 hover:underline"
              >
                戻る
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-gray-500">読み込み中...</div>
      </div>
    }>
      <LoginForm />
    </Suspense>
  );
}
